import { styled } from "@linaria/react"
import { Link, useLocation, useNavigate } from "@tanstack/react-router"
import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { useOrgContext, useQueries } from "@/hooks/useRootContext.ts"
import { CreateSemibold } from "@/icons/Icons.ts"
import { startViewTransition } from "@/lib/dom.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { useCurrentOrgId, useCurrentProjectOrNull } from "@/routes/projects/projectHooks.ts"
import { ProjectListModel } from "@/routes/projects/ProjectListModel.ts"
import { LAST_USED_PROJECT_KEY } from "@/routes/tasks/TaskNewScene/persistence.ts"
import { useNewProjectMatch, useNewTaskMatch } from "@/routes/tasks/tasksHooks.ts"

import type { ProjectKey } from "@/lib/client/generated/TypeDefinitions.ts"
import type { PropsWithChildren } from "react"


export const MainMenu = observer((props: PropsWithChildren) => {
  const orgId = useCurrentOrgId()
  const location = useLocation()
  const navigate = useNavigate()

  const newTaskMatch = useNewTaskMatch()
  const newProjectMatch = useNewProjectMatch()

  const queries = useQueries()
  const currentProjectKey = useCurrentProjectOrNull()?.key
  const [lastUsedProjectKey, setLastUsedProjectKey] = useState<ProjectKey | null>(null)

  useEffect(() => {
    void queries.getProfilePreference(`${orgId}/${LAST_USED_PROJECT_KEY}`).then((result) => result
      ? setLastUsedProjectKey({ key: result })
      : setLastUsedProjectKey(null)
    )
  }, [queries, orgId])

  // Only create the model if really needed
  const shouldCreateModel = !currentProjectKey && !lastUsedProjectKey

  const context = useOrgContext()

  const newTaskModel = useModel(
    () => (shouldCreateModel ? new ProjectListModel(context) : null),
    [shouldCreateModel, context]
  )

  const firstProjectKey =
    shouldCreateModel && newTaskModel
      ? newTaskModel.projects.get()[0]?.key ?? null
      : null

  const projectKey = currentProjectKey || lastUsedProjectKey || firstProjectKey

  const newTaskLocation = (projectKey: ProjectKey) => {
    return Navigator.newTask(orgId, projectKey, {
      closeLocation: { href: location.href }
    })
  }

  const handleNewTask = (projectKey: ProjectKey) => {
    startViewTransition(() => navigate(newTaskLocation(projectKey)))
  }

  return (
    <>
      {props.children}

      {projectKey && !newTaskMatch && !newProjectMatch && (
        <Tooltip content="New idea" side="right">
          <Link {...newTaskLocation(projectKey)} onClick={(e) => e.preventDefault()} tabIndex={-1}>
            <Button kind="icon-rounded" data-testid="new-idea-button" onClick={() => handleNewTask(projectKey)}>
              <CreateIcon />
            </Button>
          </Link>
        </Tooltip>
      )}
    </>
  )
})

const CreateIcon = styled(CreateSemibold)`
  && {
    width: 16px;
    height: 16px;
  }
`
